<div class="document-browser" *ngIf="!showViewer && !showFolderContents">
  <!-- Header with search and view toggle (optional when parent supplies global search) -->
  <div class="browser-header d-flex align-items-center justify-content-between mt-3 mb-3" *ngIf="!hideHeaderSearch">
    <div class="search-box px-3 d-flex align-items-center justify-content-between bg-white rounded-10px">
      <div class="search-field d-flex my-1">
        <input
          class="form-control text-muted border-0 fs-14"
          type="text"
          [(ngModel)]="searchValue"
          #searchResult="ngModel"
          (keyup.enter)="searchOnEnter($event)"
          placeholder="{{ 'dashboard.search.searchByKeyword' | translate }}"
        />
        @if (searchValue) {
          <button class="btn bg-transparent shadow-none border-0 pe-3" aria-label="Clear" (click)="clearSearch()">
            <mat-icon>clear</mat-icon>
          </button>
        }
      </div>

      <div class="search-btn my-1">
        <button class="btn btn-outline-primary w-100 fs-14 rounded-1" (click)="searchDocuments()">
          <span>{{ 'dashboard.search.search' | translate }}</span>
        </button>
      </div>
    </div>
  </div>

  <div
    class="personal-data-blue rounded-3 filter-toolbar"
    [ngClass]="hideHeaderSearch ? 'filter-toolbar--embedded' : 'p-3 mt-3'"
  >
    <form [formGroup]="filterForm">
      <div class="d-flex top-form-filter gap-3 align-items-end">
        <div class="filter-field">
          <label for="fileType" class="text-muted fs-14">{{ 'fileType.file' | translate }}</label>
          <select
            name="file_type"
            id="file_type"
            class="form-select rounded-1 text-muted fs-14 shadow-none"
            aria-placeholder="select"
            formControlName="file_type"
            (change)="onFilterChange($event, 'file_type')"
          >
            <option class="select-placehoder" selected value="">
              {{ 'fileType.select' | translate }}
            </option>
            @for (fileType of fileTypes; track fileType?.id || $index) {
              <option [value]="fileType?.id">
                {{ fileType?.name | titlecase }}
              </option>
            }
          </select>
        </div>
        <div class="filter-field">
          <label for="start_date" class="text-muted fs-14">{{
            'auditTrailCmeRequest.placeholder.selectDateRange' | translate
          }}</label>

          <educare-date-range-picker
            [formGroup]="filterForm"
            [placeholder]="'auditTrailCmeRequest.placeholder.selectDateRange' | translate"
            (dateChange)="applyDateFilter($event)"
          ></educare-date-range-picker>
        </div>
        <div class="filter-field">
          <label for="uploaded_by" class="text-muted fs-14">{{ 'fileType.update' | translate }}</label>
          <input
            type="text"
            name="uploaded_by"
            id="uploaded_by"
            class="form-control rounded-1 text-muted fs-14 shadow-none"
            placeholder="{{ 'fileType.name' | translate }}"
            formControlName="uploaded_by"
            (input)="onFilterChange($event, 'uploaded_by')"
          />
        </div>
      </div>
    </form>
  </div>

  <div class="browser-content personal-data-blue p-3 rounded-3">
    <div
      *ngIf="enableSelection && selectedItems.length > 0"
      class="selection-actions mb-3 d-flex justify-content-between align-items-center"
    >
      <span class="selection-count text-primary">
        {{ selectedItems.length }} {{ 'common.itemsSelected' | translate }}
      </span>
      <div class="d-flex gap-2">
        <button class="btn btn-primary" type="button" (click)="openAreaOfInterestDialog()">
          {{ 'common.areaOfInterest.label' | translate }}
        </button>
        <button class="btn btn-primary" (click)="openGrantAccessDialog(false)">
          {{ 'common.grantAccess' | translate }}
        </button>
        <button class="btn btn-primary" (click)="openGrantAccessDialog(true)">
          {{ 'common.revokeAccess' | translate }}
        </button>
      </div>
    </div>

    <div *ngIf="loading" class="loading-container d-flex justify-content-center align-items-center py-5">
      <mat-icon class="spin">refresh</mat-icon>
      <span class="ms-2">{{ 'common.loading' | translate }}</span>
    </div>

    <div *ngIf="!loading && !items.length" class="no-results text-center py-5">
      <p class="text-muted">{{ 'common.noResultsFound' | translate }}</p>
    </div>

    <div *ngIf="!loading && items.length > 0">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="fs-5 mb-0 text-muted">{{ 'common.documents' | translate }}</h5>
        <div class="view-toggle d-flex gap-2">
          <div class="filter-sort">
            <div [formGroup]="filterForm">
              <select
                name="sort_by"
                id="sort_by"
                class="form-select rounded-1 text-muted fs-14 shadow-none"
                aria-placeholder="select"
                formControlName="sort_by"
                (change)="onFilterChange($event, 'sort_by')"
              >
                <option class="select-placehoder" selected value="">
                  {{ 'sortingFilter.sortBy' | translate }}
                </option>
                <optgroup label="{{ 'sortingFilter.sortByField' | translate }}">
                  @for (sortOption of sortOptions; track sortOption?.id || $index) {
                    <option [value]="sortOption?.id">
                      {{ sortOption?.name }}
                    </option>
                  }
                </optgroup>
                <optgroup label="{{ 'sortingFilter.sortOrder' | translate }}">
                  @for (sortOrder of sortOrder; track sortOrder?.id || $index) {
                    <option [value]="sortOrder?.id">
                      {{ sortOrder?.name }}
                    </option>
                  }
                </optgroup>
              </select>
            </div>
          </div>

          <educare-view-mode-toggle
            [viewMode]="viewMode"
            (viewModeChange)="onViewModeToggle($event)"
          ></educare-view-mode-toggle>
        </div>
      </div>
    </div>

    <!-- Grid View -->
    <div *ngIf="!loading && items.length > 0 && viewMode === 'grid'" class="grid-view">
      <div class="grid-select-all mb-3" *ngIf="enableSelection">
        <mat-checkbox
          [checked]="selectAll"
          [indeterminate]="selectedItems.length > 0 && selectedItems.length < totalItems"
          (change)="onTableSelectAllAcrossPages($event.checked)"
        >
          {{ 'common.selectAll' | translate }}
        </mat-checkbox>
      </div>

      <div #gridContainer class="grid-container">
        <mat-card *ngFor="let item of items; trackBy: trackByFn" class="item-card">
          <div class="grid-item-action"></div>
          <div
            (click)="onItemClick(item)"
            (keydown.enter)="onItemClick(item)"
            (keydown.space)="onItemClick(item)"
            tabindex="0"
            role="button"
          >
            <mat-card-header
              [ngClass]="{
                'graybg-header': item.type === 'folder',
                'bluebg-header': item.type === 'document' || item.type === 'file'
              }"
              class="custom-header d-flex justify-content-center align-items-center"
            >
              <educare-file-icon
                [iconType]="getFileIcon(item)"
                size="large"
                [containerClass]="item.type === 'folder' ? 'folder-icon' : 'document-icon'"
              ></educare-file-icon>
            </mat-card-header>
            <mat-card-content class="d-flex flex-column text-start">
              <div class="d-flex">
                <div *ngIf="enableSelection" class="grid-item-checkbox">
                  <mat-checkbox
                    [checked]="isItemSelected(item)"
                    (change)="toggleItemSelection(item, $event.checked)"
                    (click)="$event.stopPropagation()"
                  ></mat-checkbox>
                </div>
                <div class="grid-content">
                  <h6 class="item-name fs-18 mt-2 mb-1" [matTooltip]="item.name">{{ item.name }}</h6>
                  <div class="item-details">
                    <p class="fs-12 mb-1">{{ item.updatedDate }}</p>
                    <p class="fs-12 mb-0">
                      <span *ngIf="item.type === 'folder'">{{ item.fileCount }} {{ 'common.files' | translate }}</span>
                      <span *ngIf="item.type === 'file' || item.type === 'document'"></span>
                    </p>
                  </div>
                </div>
                <div *ngIf="showActions" class="d-flex gap-2">
                  <button
                    [matTooltip]="'common.areaOfInterest.label' | translate"
                    matTooltipPosition="below"
                    class="action-btn"
                    (click)="openAreaOfInterestDialog([item.id]); $event.stopPropagation()"
                    [disabled]="selectedItems.length > 0"
                  >
                    <i class="das das-bookmark_heart"></i>
                  </button>
                  <!-- Grant Access Button -->
                  <button
                    [matTooltip]="'common.grantAccess' | translate"
                    matTooltipPosition="below"
                    class="action-btn"
                    (click)="openGrantAccessDialog(false, item); $event.stopPropagation()"
                    [disabled]="selectedItems.length > 0"
                  >
                    <i class="das das-article_person"></i>
                  </button>

                  <!-- Revoke Access Button -->
                  <button
                    [matTooltip]="'common.revokeAccess' | translate"
                    matTooltipPosition="below"
                    class="action-btn"
                    (click)="openGrantAccessDialog(true, item); $event.stopPropagation()"
                    [disabled]="selectedItems.length > 0"
                  >
                    <i class="das das-undo"></i>
                  </button>
                </div>
              </div>
            </mat-card-content>
          </div>
        </mat-card>
      </div>

      <!-- Pagination for Grid View -->
      <div class="grid-footer d-flex justify-content-end align-items-center mt-3">
        <mat-paginator
          *ngIf="showPagination"
          [length]="totalItems"
          [pageSize]="pageSize"
          [pageIndex]="currentPage"
          [hidePageSize]="true"
          (page)="onPageChange($event)"
          showFirstLastButtons="true"
        ></mat-paginator>
      </div>
    </div>

    <!-- List View -->
    <div *ngIf="!loading && items.length > 0 && viewMode === 'list'" class="list-view">
      <div class="table-page">
        <educare-table
          [dataSource]="tableDataSource"
          [columns]="columns"
          [selectionConfig]="tableSelectionConfig"
          (pageChange)="onPageChange($event)"
          (selectionChange)="onTableSelectionChange($event)"
          (selectAllAcrossPages)="onTableSelectAllAcrossPages($event)"
        ></educare-table>
      </div>
    </div>
  </div>
</div>

<educare-folder-contents
  *ngIf="showFolderContents && selectedFolder && !showViewer"
  [folderId]="selectedFolder.id"
  [folderName]="selectedFolder.name"
  [viewMode]="viewMode"
  (back)="onFolderBack()"
  (itemClick)="onFolderItemClick($event)"
  (selectionChange)="onFolderSelectionChange($event)"
>
</educare-folder-contents>

<educare-document-viewer *ngIf="showViewer && selectedDocument" [document]="selectedDocument" (back)="onViewerBack()">
</educare-document-viewer>
